import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { Modal, Button, Form, Alert, Spinner, Row, Col, Badge } from 'react-bootstrap';
import { sessionStore } from '../stores/SessionStore';
import { activityStore } from '../stores/ActivityStore';
import { inventoryStore } from '../stores/InventoryStore';
import { batchStore } from '../stores/BatchStore';
import { academyStore } from '../stores/AcademyStore';
import UserSelectionDialog from './UserSelectionDialog';
const SessionTemplateDialog = observer(() => {
    const [formData, setFormData] = useState({
        title: '',
        duration: ''
    });
    const [coaches, setCoaches] = useState([]);
    const [players, setPlayers] = useState([]);
    const [activities, setActivities] = useState([]);
    const [inventories, setInventories] = useState([]);
    const [batches, setBatches] = useState([]);
    const [errors, setErrors] = useState({});
    const [coachesDialogOpen, setCoachesDialogOpen] = useState(false);
    const [playersDialogOpen, setPlayersDialogOpen] = useState(false);
    useEffect(() => {
        if (sessionStore.isEditingTemplate && sessionStore.selectedTemplate) {
            const template = sessionStore.selectedTemplate;
            setFormData({
                title: template.title,
                duration: template.duration.toString()
            });
            setCoaches(template.coaches || []);
            setPlayers(template.players || []);
            setActivities(template.activities || []);
            setInventories(template.inventories || []);
            setBatches(template.batches || []);
        }
        else {
            setFormData({
                title: '',
                duration: ''
            });
            setCoaches([]);
            setPlayers([]);
            setActivities([]);
            setInventories([]);
            setBatches([]);
        }
        setErrors({});
    }, [sessionStore.isTemplateDialogOpen, sessionStore.isEditingTemplate, sessionStore.selectedTemplate]);
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };
    const validateForm = () => {
        const newErrors = {};
        if (!formData.title.trim()) {
            newErrors.title = 'Title is required';
        }
        else if (formData.title.trim().length < 2) {
            newErrors.title = 'Title must be at least 2 characters long';
        }
        if (!formData.duration.trim()) {
            newErrors.duration = 'Duration is required';
        }
        else {
            const duration = parseInt(formData.duration);
            if (isNaN(duration) || duration < 15) {
                newErrors.duration = 'Duration must be at least 15 minutes';
            }
            else if (duration > 1440) {
                newErrors.duration = 'Duration cannot exceed 1440 minutes (24 hours)';
            }
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) {
            return;
        }
        try {
            const templateData = {
                title: formData.title.trim(),
                duration: parseInt(formData.duration),
                coaches,
                activities,
                inventories,
                batches,
                players
            };
            if (sessionStore.isEditingTemplate && sessionStore.selectedTemplate) {
                await sessionStore.updateTemplate(sessionStore.selectedTemplate.id, templateData);
            }
            else {
                await sessionStore.createTemplate(templateData);
            }
            sessionStore.closeTemplateDialog();
        }
        catch (error) {
            // Error handled by store
        }
    };
    const handleClose = () => {
        sessionStore.closeTemplateDialog();
    };
    const handleSaveCoaches = async (selectedIds) => {
        setCoaches([...coaches, ...selectedIds]);
    };
    const handleSavePlayers = async (selectedIds) => {
        setPlayers([...players, ...selectedIds]);
    };
    const academyId = academyStore.currentAcademy?.id || '';
    return (_jsxs(_Fragment, { children: [_jsxs(Modal, { show: sessionStore.isTemplateDialogOpen, onHide: handleClose, size: "lg", children: [_jsx(Modal.Header, { closeButton: true, children: _jsx(Modal.Title, { children: sessionStore.isEditingTemplate ? 'Edit Session Template' : 'Add New Session Template' }) }), _jsxs(Form, { onSubmit: handleSubmit, children: [_jsxs(Modal.Body, { style: { maxHeight: '70vh', overflowY: 'auto' }, children: [sessionStore.hasError && (_jsx(Alert, { variant: "danger", className: "mb-3", children: sessionStore.error })), _jsxs(Row, { children: [_jsx(Col, { md: 6, children: _jsxs(Form.Group, { className: "mb-3", children: [_jsx(Form.Label, { children: "Title *" }), _jsx(Form.Control, { type: "text", name: "title", value: formData.title, onChange: handleInputChange, isInvalid: !!errors.title, placeholder: "e.g., Bowling practice", disabled: sessionStore.isLoading }), _jsx(Form.Control.Feedback, { type: "invalid", children: errors.title })] }) }), _jsx(Col, { md: 6, children: _jsxs(Form.Group, { className: "mb-3", children: [_jsx(Form.Label, { children: "Duration (minutes) *" }), _jsx(Form.Control, { type: "number", name: "duration", value: formData.duration, onChange: handleInputChange, isInvalid: !!errors.duration, placeholder: "e.g., 120", min: "15", max: "1440", disabled: sessionStore.isLoading }), _jsx(Form.Control.Feedback, { type: "invalid", children: errors.duration })] }) })] }), _jsxs(Row, { children: [_jsx(Col, { md: 6, children: _jsxs(Form.Group, { className: "mb-3", children: [_jsx(Form.Label, { children: "Coaches" }), _jsxs("div", { className: "d-flex gap-2 align-items-center", children: [_jsxs(Badge, { bg: "info", children: [coaches.length, " selected"] }), _jsx(Button, { variant: "outline-primary", size: "sm", onClick: () => setCoachesDialogOpen(true), children: "Select Coaches" })] })] }) }), _jsx(Col, { md: 6, children: _jsxs(Form.Group, { className: "mb-3", children: [_jsx(Form.Label, { children: "Players" }), _jsxs("div", { className: "d-flex gap-2 align-items-center", children: [_jsxs(Badge, { bg: "success", children: [players.length, " selected"] }), _jsx(Button, { variant: "outline-success", size: "sm", onClick: () => setPlayersDialogOpen(true), children: "Select Players" })] })] }) })] }), _jsx(Row, { children: _jsx(Col, { md: 12, children: _jsxs(Form.Group, { className: "mb-3", children: [_jsx(Form.Label, { children: "Activities" }), _jsx(Form.Select, { multiple: true, value: activities, onChange: (e) => {
                                                            const selected = Array.from(e.target.selectedOptions, option => option.value);
                                                            setActivities(selected);
                                                        }, disabled: sessionStore.isLoading, children: activityStore.activities.map(activity => (_jsx("option", { value: activity.id, children: activity.name }, activity.id))) }), _jsx(Form.Text, { className: "text-muted", children: "Hold Ctrl/Cmd to select multiple" })] }) }) }), _jsx(Row, { children: _jsx(Col, { md: 12, children: _jsxs(Form.Group, { className: "mb-3", children: [_jsx(Form.Label, { children: "Inventories" }), _jsx(Form.Select, { multiple: true, value: inventories, onChange: (e) => {
                                                            const selected = Array.from(e.target.selectedOptions, option => option.value);
                                                            setInventories(selected);
                                                        }, disabled: sessionStore.isLoading, children: inventoryStore.inventories.filter(i => i.active).map(inventory => (_jsx("option", { value: inventory.id, children: inventory.name }, inventory.id))) })] }) }) }), _jsx(Row, { children: _jsx(Col, { md: 12, children: _jsxs(Form.Group, { className: "mb-3", children: [_jsx(Form.Label, { children: "Batches" }), _jsx(Form.Select, { multiple: true, value: batches, onChange: (e) => {
                                                            const selected = Array.from(e.target.selectedOptions, option => option.value);
                                                            setBatches(selected);
                                                        }, disabled: sessionStore.isLoading, children: batchStore.batches.map(batch => (_jsx("option", { value: batch.id, children: batch.name }, batch.id))) })] }) }) })] }), _jsxs(Modal.Footer, { children: [_jsx(Button, { variant: "secondary", onClick: handleClose, disabled: sessionStore.isLoading, className: "btn-touch w-mobile-100", children: "Cancel" }), _jsxs(Button, { variant: "primary", type: "submit", disabled: sessionStore.isLoading, className: "btn-touch w-mobile-100", children: [sessionStore.isLoading && (_jsx(Spinner, { animation: "border", size: "sm", className: "me-2" })), sessionStore.isEditingTemplate ? 'Update' : 'Create'] })] })] })] }), coachesDialogOpen && (_jsx(UserSelectionDialog, { show: coachesDialogOpen, onHide: () => setCoachesDialogOpen(false), onSave: handleSaveCoaches, title: "Select Coaches", userRole: "coach", currentUserIds: coaches, academyId: academyId })), playersDialogOpen && (_jsx(UserSelectionDialog, { show: playersDialogOpen, onHide: () => setPlayersDialogOpen(false), onSave: handleSavePlayers, title: "Select Players", userRole: "player", currentUserIds: players, academyId: academyId }))] }));
});
export default SessionTemplateDialog;
